import React, { useState, useEffect, useCallback } from "react";
import { motion } from "motion/react";
import {
  FileText, CheckCircle, Lightbulb, Lock, Target, Save, X
} from "lucide-react";
import { useConsultation } from "../../context/ConsultationContext";
import { useAutoSave } from "../../hooks/useAutoSave";
import AutoSaveIndicator from "./AutoSaveIndicator";
import { supabase } from "../../lib/supabase";

export default function ExpertNotesForm({ booking, onClose, onSaved }) {
  const { saveExpertNotes, getExpertNotes } = useConsultation();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);

  const [form, setForm] = useState({
    summary: "",
    recommendations: "",
    homework: "",
    private_note: "",
    next_focus: ""
  });

  const buildPayload = useCallback((formData) => ({
    summary: formData.summary,
    recommendations: formData.recommendations,
    homework: formData.homework || "",
    private_note: formData.private_note || "",
    next_focus: formData.next_focus || ""
  }), []);

  const buildInsertPayload = useCallback((formData) => ({
    summary: formData.summary,
    recommendations: formData.recommendations,
    homework: formData.homework || "",
    private_note: formData.private_note || "",
    next_focus: formData.next_focus || "",
    booking_id: booking.id,
    expert_id: booking.expertId,
    client_id: booking.clientId,
    expert_name: booking.expertName || "",
    client_name: booking.clientName || "",
    session_date: booking.date || "",
    session_topic: booking.topic || ""
  }), [booking]);

  const {
    autoSaving,
    lastAutoSaved,
    restoredFromDraft,
    setRestoredFromDraft,
    scheduleAutoSave,
    cancelAutoSave,
    markSaved,
    loadFromLocal
  } = useAutoSave({
    table: "consultation_notes",
    bookingId: booking.id,
    form,
    buildPayload,
    buildInsertPayload,
    enabled: !loadingExisting
  });

  useEffect(() => {
    const load = async () => {
      const existing = await getExpertNotes(booking.id);
      if (existing) {
        setForm({
          summary: existing.summary || "",
          recommendations: existing.recommendations || "",
          homework: existing.homework || "",
          private_note: existing.private_note || "",
          next_focus: existing.next_focus || ""
        });
      } else {
        const localDraft = loadFromLocal();
        if (localDraft) {
          setForm(localDraft);
          setRestoredFromDraft(true);
        }
      }
      setLoadingExisting(false);
    };
    load();
  }, [booking.id, getExpertNotes, loadFromLocal, setRestoredFromDraft]);

  const handleSave = async () => {
    if (!form.summary.trim() || !form.recommendations.trim()) return;
    cancelAutoSave();
    setSaving(true);
    const result = await saveExpertNotes({
      booking_id: booking.id,
      expert_id: booking.expertId,
      client_id: booking.clientId,
      expert_name: booking.expertName || "",
      client_name: booking.clientName || "",
      session_date: booking.date || "",
      session_topic: booking.topic || "",
      summary: form.summary,
      recommendations: form.recommendations,
      homework: form.homework,
      private_note: form.private_note,
      next_focus: form.next_focus
    });
    setSaving(false);
    if (result) {
      markSaved();
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        if (onSaved) onSaved(result);
      }, 1500);
    }
  };

  const update = (field, value) => {
    const newForm = { ...form, [field]: value };
    setForm(newForm);
    scheduleAutoSave(newForm);
  };

  if (loadingExisting) {
    return (
      <div id="expert-notes-form_loading" className="p-6 text-center">
        <p className="text-lg font-extralight text-neutral-400">Загрузка...</p>
      </div>
    );
  }

  const fields = [
    {
      key: "summary",
      label: "Краткий итог сессии",
      placeholder: "Опишите основные темы, которые обсудили с клиентом",
      icon: FileText,
      required: true,
      rows: 3
    },
    {
      key: "recommendations",
      label: "Ключевые рекомендации",
      placeholder: "Какие конкретные шаги вы рекомендовали клиенту?",
      icon: CheckCircle,
      required: true,
      rows: 3
    },
    {
      key: "homework",
      label: "Домашнее задание",
      placeholder: "Что клиенту стоит сделать до следующей сессии?",
      icon: Lightbulb,
      required: false,
      rows: 2
    },
    {
      key: "private_note",
      label: "Приватная заметка (только для вас)",
      placeholder: "На что обратить внимание в следующий раз",
      icon: Lock,
      required: false,
      rows: 2,
      isPrivate: true
    },
    {
      key: "next_focus",
      label: "Следующий фокус",
      placeholder: "Что планируете разобрать на следующей консультации?",
      icon: Target,
      required: false,
      rows: 2
    }
  ];

  return (
    <motion.div
      id="expert-notes-form_container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 rounded-md bg-neutral-900 border border-neutral-800"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-medium text-white">
            Итог консультации
          </h3>
          <p className="text-lg font-extralight text-neutral-400 mt-1">
            {booking.clientName || "Клиент"} — {booking.topic || "Без темы"} — {booking.date}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <AutoSaveIndicator
            autoSaving={autoSaving}
            lastAutoSaved={lastAutoSaved}
            restoredFromDraft={restoredFromDraft}
            onDismissRestore={() => setRestoredFromDraft(false)}
          />
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-md hover:bg-neutral-800 text-neutral-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      <div className="space-y-5">
        {fields.map((field) => (
          <div key={field.key}>
            <label className="flex items-center gap-2 text-lg font-extralight text-neutral-300 mb-2">
              <field.icon className={`w-4 h-4 ${field.isPrivate ? "text-yellow-400" : "text-primary-400"}`} />
              {field.label}
              {field.required && <span className="text-red-400">*</span>}
              {field.isPrivate && (
                <span className="px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400 text-lg font-extralight">
                  Приватно
                </span>
              )}
            </label>
            <textarea
              value={form[field.key]}
              onChange={(e) => update(field.key, e.target.value)}
              rows={field.rows}
              placeholder={field.placeholder}
              className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-500 resize-none transition-colors"
            />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-6">
        <div>
          {saved && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-2 text-accent-400"
            >
              <CheckCircle className="w-5 h-5" />
              <span className="text-lg font-extralight">Заметки сохранены</span>
            </motion.div>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !form.summary.trim() || !form.recommendations.trim()}
          className="flex items-center gap-2 px-6 py-3 rounded-md bg-primary-600 hover:bg-primary-500 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white text-lg font-medium transition-colors"
        >
          <Save className="w-5 h-5" />
          {saving ? "Сохранение..." : "Сохранить"}
        </button>
      </div>
    </motion.div>
  );
}
